<template>
  <v-card class="mb-3">
    <v-card-title class="d-flex align-center ga-2">
      <span>Atribuir a Usuários</span>
      <t-tooltip>
        Somente os usuários selecionados poderão resgatar o cupom. Se nenhum for selecionado, qualquer usuário
        poderá realizar o resgate
      </t-tooltip>
    </v-card-title>
    <v-card-text>
      <v-row v-show="!readonly">
        <v-col cols="3">
          <v-text-field
            v-model="form.id"
            label="ID"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            v-model="form.email"
            label="Email"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
          />
        </v-col>
        <v-col cols="3">
          <v-text-field
            :model-value="form.document"
            label="Documento"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="fetch"
            @update:model-value="form.document = ($event ?? '').toUpperCase()"
          />
        </v-col>
        <v-col cols="3" class="d-flex align-center ga-2">
          <v-file-input
            v-model="form.file"
            accept="text/csv"
            :show-size="false"
            label="Arquivo CSV"
            variant="outlined"
            density="compact"
            hide-details
            prepend-icon=""
            prepend-inner-icon="fa-regular fa-file-csv"
          />
          <t-tooltip location="top" @click="downloadExampleFile">
            <div>Arquivo CSV com uma única coluna contendo todos os usuários que poderão resgatar o cupom.</div>
            <div>Cada linha deve conter apenas um CPF, passaporte ou email do usuário.</div>
            <div>Ex:</div>
            <div v-for="line in exampleFileContent.split('\n')" :key="line">
              {{ line }}
            </div>
          </t-tooltip>
        </v-col>
        <v-col cols="3">
          <v-btn
            :loading="$loader.loading"
            block
            color="primary"
            @click="fetch"
          >
            Buscar
          </v-btn>
        </v-col>
      </v-row>

      <template v-if="pagination.items.length > 0">
        <v-table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Documento</th>
              <th>Email</th>
              <th>Atribuir</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in pagination.items" :key="user.id">
              <td>{{ user.id.split("-")[0] }}</td>
              <td>{{ user.name }}</td>
              <td>{{ $formatters.document(user.document, user.document_type) }}</td>
              <td>{{ user.email }}</td>
              <td class="text-center">
                <v-checkbox
                  :model-value="isAssigned(user)"
                  hide-details
                  multiple
                  :readonly="readonly"
                  @update:model-value="onCheck(user)"
                />
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5" class="pb-1 text-end">
                <v-btn
                  v-if="unassigned.length"
                  color="primary"
                  :disabled="$loader.loading"
                  @click="unassigned = []"
                >
                  Marcar Todos
                </v-btn>
                <v-btn
                  v-else
                  color="primary"
                  :disabled="$loader.loading"
                  @click="unassigned = searchParams"
                >
                  Desmarcar Todos
                </v-btn>
              </td>
            </tr>
          </tfoot>
        </v-table>
        <v-pagination
          v-model="pagination.page"
          :total-visible="5"
          :length="pagination.totalPages"
          @update:model-value="fetch"
        />
      </template>
    </v-card-text>
  </v-card>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { AxiosError } from 'axios'
import Coupon from '@/types/Coupon'
import User from '@/types/User'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import CouponService from '@/services/CouponService'
import TTooltip from '@/components/TTooltip.vue'

interface Props {
  readonly?: boolean
  editing?: boolean
}

interface Form {
  id: string
  email: string
  document: string
  file?: File
}

interface Pagination {
  items: User[]
  page: number
  itemsPerPage: number
  totalPages: number
}

const loader = useLoader()
const snackbar = useSnackBar()

const service = new CouponService()

const emit = defineEmits<{
  check: [users: string[]]
}>()

const { readonly } = defineProps<Props>()

defineModel<Coupon>({ required: true })

const form = ref<Form>({ id: '', email: '', document: '' })

const unassigned = ref<string[]>([])

const pagination = ref<Pagination>({ items: [], page: 1, itemsPerPage: 5, totalPages: 0 })

const searchParams = ref<string[]>([])

function isAssigned(user: User) {
  return !unassigned.value.some(entry => [user.id, user.email, user.document].includes(entry))
}

function onCheck(user: User) {
  if (unassigned.value.some(entry => [user.id, user.email, user.document].includes(entry))) {
    unassigned.value = unassigned.value.filter(entry => ![user.id, user.email, user.document].includes(entry))
  } else {
    unassigned.value.push(user.id, user.email, user.document)
  }

  emit('check', searchParams.value.filter(entry => !unassigned.value.includes(entry)))
}

async function updateSearchParams() {
  const newParams = [
    form.value.id,
    form.value.email,
    form.value.document,
    ...await getFileValues(),
  ].map(v => v.trim()).filter(v => v !== '')

  searchParams.value = [
    ...newParams,
    ...searchParams.value.filter(v => !newParams.includes(v)),
  ]

  emit('check', searchParams.value.filter(entry => !unassigned.value.includes(entry)))
}

async function fetch() {
  loader.increment()
  try {
    await updateSearchParams()

    const response = await service.searchUsers(searchParams.value, pagination.value.page, pagination.value.itemsPerPage)

    pagination.value.items = response.data.data
    pagination.value.page = response.data.current_page ?? 1
    pagination.value.totalPages = response.data.last_page ?? 1

    clearForm()

    if (pagination.value.items.length === 0) {
      snackbar.show('Nenhum usuário encontrado.', 'info')
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      snackbar.show(error.response?.data?.message, 'error')
    } else if (error instanceof Error) {
      snackbar.show(error.message, 'error')
    } else {
      snackbar.show('Não foi possível carregar os usuários', 'error')
    }
    console.log(error)
  } finally {
    loader.decrement()
  }
}

async function getFileValues() {
  if (!form.value.file) return []
  return (await form.value.file.text()).split('\n') ?? []
}

function clearForm() {
  form.value.id = ''
  form.value.email = ''
  form.value.document = ''
  form.value.file = undefined
}

const exampleFileContent = '07068093868\nfulano@gmail.com\n00140059059\n51002095000'

function downloadExampleFile() {
  const blob = new Blob([ exampleFileContent ], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = 'usuarios_exemplo.csv'
  a.click()

  URL.revokeObjectURL(url)
}

</script>
